'use client'

import { FC, useEffect, useLayoutEffect, useRef } from 'react'
import { useGetTranslations, useSetAuthToken, useCustomRouter } from '@/root/shared-hooks'
import { Banner, Button } from '@/root/ui/tuuleliinid'
import { SmartIdIcon } from '@/root/ui/tuuleliinid/icons/SmartIdIcon'
import type { AuthVariant } from '../../constants'
import { useSmartIdFlow } from './useSmartIdFlow'

interface SmartIdAuthProps {
  variant: AuthVariant
  onLoadingChange?: (isLoading: boolean) => void
  onSuccess?: (accessToken: string, personalIdentificationNumber: string) => void
}

export const SmartIdAuth: FC<SmartIdAuthProps> = ({ variant, onLoadingChange, onSuccess }) => {
  const { getTranslation } = useGetTranslations()
  const { saveToken } = useSetAuthToken()
  const { push, locale } = useCustomRouter()

  // useLatest refs — keep fresh values accessible inside the long-running async callback
  // without adding them to effect dependencies
  const pushRef = useRef(push)
  const saveTokenRef = useRef(saveToken)
  const localeRef = useRef(locale)
  const onSuccessRef = useRef(onSuccess)
  const pendingPinRef = useRef('')
  useLayoutEffect(() => {
    pushRef.current = push
  }, [push])
  useLayoutEffect(() => {
    saveTokenRef.current = saveToken
  }, [saveToken])
  useLayoutEffect(() => {
    localeRef.current = locale
  }, [locale])
  useLayoutEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  const isSignup = variant === 'signup'
  const { errorMessage, handleSubmit: handleFlowSubmit, isLoading, isPreparing, isWaitingForConfirmation, verificationCode } =
    useSmartIdFlow({
      onSuccess: async (accessToken) => {
        if (onSuccessRef.current) {
          onSuccessRef.current(accessToken, pendingPinRef.current)
          return
        }
        await saveTokenRef.current(accessToken)
        await pushRef.current(`/${localeRef.current}/my/details`)
      },
      messages: {
        invalidRequest: getTranslation('authSmartIdInvalidRequest'),
        prepareError: getTranslation('authSmartIdPrepareError'),
        performError: getTranslation('authSmartIdPerformError'),
        missingSessionId: getTranslation('authSmartIdMissingSessionId'),
      },
    })

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const buttonText = isPreparing
    ? getTranslation('authSmartIdPreparing')
    : isSignup
      ? getTranslation('authSmartIdSignup')
      : getTranslation('authSmartIdLogin')

  if (isWaitingForConfirmation && verificationCode) {
    return (
      <div className="flex w-full flex-col items-center gap-4">
        <SmartIdIcon width={24} height={24} />
        <p className="text-primary-base text-lg font-bold">{getTranslation('authSmartIdTitle')}</p>
        <p className="text-base-normal text-sm">{getTranslation('authSmartIdWaiting')}</p>
        <p className="text-base-normal text-xs">{getTranslation('authSmartIdVerificationCode')}</p>
        <p className="text-primary-base text-4xl font-bold tracking-widest">{verificationCode}</p>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center">
      <SmartIdIcon width={24} height={24} />

      <p className="text-primary-base mt-2 text-lg font-bold">{getTranslation('authSmartIdTitle')}</p>

      <p className="text-base-normal mt-2 text-center text-sm">{getTranslation('authSmartIdDescription')}</p>

      {errorMessage ? <Banner variant="error" align="left" body={errorMessage} className="mt-4 mb-2 w-full" /> : null}

      <form onSubmit={(e) => { pendingPinRef.current = String(new FormData(e.currentTarget).get('personalIdentificationNumber') ?? ''); handleFlowSubmit(e) }} className="mt-6 flex w-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="smartid-pid" className="text-base-normal text-sm">
            {getTranslation('authSmartIdPersonalIdLabel')}
          </label>
          <input
            id="smartid-pid"
            name="personalIdentificationNumber"
            type="text"
            inputMode="numeric"
            pattern="\d{11}"
            maxLength={11}
            placeholder={getTranslation('authSmartIdPersonalIdPlaceholder')}
            disabled={isLoading}
            className="border-base-medium-light focus:border-primary-base rounded-lg border px-3 py-2 text-sm transition-colors outline-none disabled:opacity-50"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="smartid-country" className="text-base-normal text-sm">
            {getTranslation('authSmartIdCountryLabel')}
          </label>
          <select
            id="smartid-country"
            name="country"
            defaultValue="EE"
            disabled={isLoading}
            className="border-base-medium-light focus:border-primary-base rounded-lg border px-3 py-2 text-sm transition-colors outline-none disabled:opacity-50"
          >
            <option value="EE">{getTranslation('authSmartIdCountryEE')}</option>
            <option value="LV">{getTranslation('authSmartIdCountryLV')}</option>
            <option value="LT">{getTranslation('authSmartIdCountryLT')}</option>
          </select>
        </div>

        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          className="flex h-12 w-full items-center justify-center rounded-2xl text-lg font-bold whitespace-nowrap disabled:hover:brightness-100"
        >
          {buttonText}
        </Button>
      </form>
    </div>
  )
}
